/**
 * Shared date formatters (UI Design Standards §5.2).
 *
 * Calendar dates (week_ending, field_start_date, due dates) and timestamps
 * (changed_at, synced_at) use different functions. Calendar dates go through
 * calendarDate.js so they never shift a day in US time zones (#226).
 * Every function returns EM_DASH for null, empty or invalid input.
 */
import { format, isValid, parseISO } from 'date-fns';
import { addCalendarDays, toCalendarDate } from './calendarDate';

export const EM_DASH = '—';

const formatCalendar = (value, pattern) => {
  const date = toCalendarDate(value);
  return date ? format(date, pattern) : EM_DASH;
};

/**
 * Calendar date as MM/dd/yyyy, e.g. "02/10/2026". For inputs and grid cells.
 * @param {string|Date|null|undefined} value YYYY-MM-DD (or a string starting with it) or a Date
 * @returns {string}
 */
export const formatDate = (value) => formatCalendar(value, 'MM/dd/yyyy');

/**
 * Calendar date as MM/dd/yy, e.g. "06/05/26". For Kanban cards.
 * @param {string|Date|null|undefined} value
 * @returns {string}
 */
export const formatShortDate = (value) => formatCalendar(value, 'MM/dd/yy');

/**
 * Timestamp in local time as "MMM d, yyyy h:mm a", e.g. "Jun 1, 2026 1:53 PM".
 * For timestamps only (changed_at, synced_at). Do NOT pass calendar dates:
 * "2026-02-10" parses as UTC midnight and shows as Feb 9 in US time zones.
 * Use formatDate for those.
 * @param {string|Date|null|undefined} value ISO string or Date
 * @returns {string}
 */
export const formatDateTime = (value) => {
  if (value === null || value === undefined || value === '') return EM_DASH;
  let date;
  if (typeof value === 'string') date = parseISO(value.trim());
  else if (value instanceof Date) date = value;
  else return EM_DASH;
  return isValid(date) ? format(date, 'MMM d, yyyy h:mm a') : EM_DASH;
};

/**
 * Monday–Sunday label for a Sunday week-ending date, e.g. "Oct 5 – Oct 11".
 * @param {string|Date|null|undefined} weekEnding
 * @returns {string}
 */
export const formatWeekRange = (weekEnding) => {
  const end = toCalendarDate(weekEnding);
  if (!end) return EM_DASH;
  const start = toCalendarDate(addCalendarDays(end, -6));
  return `${format(start, 'MMM d')} – ${format(end, 'MMM d')}`;
};
